import classNames from 'classnames'
import React from 'react'
import { PortableTextBodyElement } from '../../utilities/blockContent'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Grid } from '../Grid'
import { BlockArea } from '../PortableText/BlockArea'
import { getTwoColumnsOneStickyComponentTypes } from '../PortableText/components/twoColumnsOneSticky'
import { RichTextArea } from '../PortableText/RichTextArea'
import { FactBoxSerializer } from '../PortableText/serializers/componentTypes/FactBoxSerializer'
import { QuoteListSerializer } from '../PortableText/serializers/componentTypes/QuoteListSerializer'
import { StickyItem } from '../StickyItem'

export interface TwoColumnsOneStickyProps {
  blocks?: PortableTextBodyElement[]
  body?: PortableTextBodyElement[]
  hasBackground?: boolean
  isMirrored?: boolean
  pageHasStickyMenu?: boolean
  preamble?: string
  title?: string
}

export const TwoColumnsOneSticky = ({
  blocks,
  body,
  hasBackground,
  isMirrored,
  pageHasStickyMenu,
  preamble,
  title,
}: TwoColumnsOneStickyProps): JSX.Element => {
  const componentTypes = getTwoColumnsOneStickyComponentTypes()

  const leftColumnClassName = classNames(
    'md:row-start-1 md:col-start-1 md:col-span-3 md:mx-0',
    {
      'md:mr-30': isMirrored,
    },
    'lg:-ml-75',
    {
      'lg:mr-60': isMirrored,
    },
    'lg:col-start-2 lg:col-span-3',
    'xl:col-start-2 xl:col-span-4 xl:-ml-45',
    'xxl:col-start-4 xxl:col-span-4'
  )

  const rightColumnClassName = classNames(
    'md:col-start-4 md:col-span-3 md:mx-0',
    {
      'md:ml-30': !isMirrored,
    },
    'lg:col-start-5 lg:col-span-3 lg:-mr-75',
    {
      'lg:ml-60': !isMirrored,
    },
    'xl:col-start-6 xl:col-span-4 xl:-mr-45',
    'xxl:col-start-8 xxl:col-span-4'
  )

  return (
    <Grid
      additionalClassName={
        hasBackground ? 'py-60 sm:py-90 lg:py-120 bg-shade-lightest-warm' : undefined
      }
    >
      {(!!body?.length ||
        !isNullOrWhiteSpace(title) ||
        !isNullOrWhiteSpace(preamble)) && (
        <div
          className={classNames(
            'col-span-full',
            'sm:mx-120',
            isMirrored ? rightColumnClassName : leftColumnClassName
          )}
        >
          {(!isNullOrWhiteSpace(title) || !isNullOrWhiteSpace(preamble)) && (
            <header className="flex flex-col gap-20 mb-30">
              {!isNullOrWhiteSpace(title) && (
                <h2 className="font-h2 text-h2 text-primary">{title}</h2>
              )}

              {!isNullOrWhiteSpace(preamble) && (
                <p className="font-preamble text-preamble text-secondary-darkest">
                  {preamble}
                </p>
              )}
            </header>
          )}

          {!!body?.length && <RichTextArea value={body} />}
        </div>
      )}

      {!!blocks?.length && (
        <div
          className={classNames(
            'hidden',
            'md:flex md:flex-col',
            isMirrored ? leftColumnClassName : rightColumnClassName
          )}
        >
          {blocks.map((block, index) => {
            let component: JSX.Element | undefined

            switch (block._type) {
              case 'factBox':
                component = <FactBoxSerializer {...block} />
                break
              case 'quoteList':
                component = <QuoteListSerializer {...block} />
                break
            }

            // Wrap each block in a StickyItem
            return (
              <StickyItem
                key={index}
                offsetBase={40}
                pageHasStickyMenu={pageHasStickyMenu}
              >
                {component}
              </StickyItem>
            )
          })}
        </div>
      )}

      {!!blocks?.length && (
        <div
          className={classNames(
            'col-span-full mt-45 space-y-30',
            'tb:mx-75',
            'sm:mx-[11.25rem]',
            'md:hidden'
          )}
        >
          <BlockArea componentTypes={componentTypes} value={blocks} />
        </div>
      )}
    </Grid>
  )
}
